import React from "react";

export const EXISTING_ACTIVE_IDENTITY_CODE =
  "EXISTING_ACTIVE_IDENTITY_CONFIRMATION_REQUIRED";

export const DEFAULT_EXISTING_ACTIVE_IDENTITY_MESSAGE =
  "active identity already exists; confirmation is required to link the canonical record";

export type ExistingActiveIdentityError = {
  code?: string;
  confirmation_field?: string;
  error?: string;
  message?: string;
  requires_confirmation?: boolean;
  existing_active_identity?: Record<string, unknown>;
};

export function isExistingActiveIdentityConfirmation(
  status: number,
  data?: ExistingActiveIdentityError | null
): boolean {
  if (status !== 409 || !data) return false;
  return (
    data.code === EXISTING_ACTIVE_IDENTITY_CODE ||
    data.requires_confirmation === true ||
    data.confirmation_field === "confirm_existing_active_identity"
  );
}

export function existingActiveIdentityMessage(
  data?: ExistingActiveIdentityError | null
): string {
  return (
    data?.error ||
    data?.message ||
    DEFAULT_EXISTING_ACTIVE_IDENTITY_MESSAGE
  );
}

/** Retry payload: confirmation_field from API + requires_confirmation as requested. */
export function withExistingActiveIdentityConfirmation(
  payload: Record<string, unknown>,
  data?: ExistingActiveIdentityError | null
): Record<string, unknown> {
  const confirmationField =
    data?.confirmation_field || "confirm_existing_active_identity";
  return {
    ...payload,
    requires_confirmation: true,
    [confirmationField]: true,
  };
}

type ExistingActiveIdentityConfirmModalProps = {
  open: boolean;
  message?: string;
  confirming?: boolean;
  closeLabel?: string;
  confirmLabel?: string;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
};

/**
 * Shared 409 confirmation for add/edit of brokerage, agents, teammembers, etc.
 * Close navigates away; Ok retries the same save with confirmation flags.
 */
const ExistingActiveIdentityConfirmModal: React.FC<
  ExistingActiveIdentityConfirmModalProps
> = ({
  open,
  message = DEFAULT_EXISTING_ACTIVE_IDENTITY_MESSAGE,
  confirming = false,
  closeLabel = "Close",
  confirmLabel = "Ok",
  onClose,
  onConfirm,
}) => {
  if (!open) return null;

  return (
    <div className="eai-overlay" role="presentation">
      <div className="eai-modal" role="dialog" aria-modal="true" aria-labelledby="eai-title">
        <h4 id="eai-title" className="eai-title">
          Confirmation required
        </h4>
        <p className="eai-text">{message}</p>
        <div className="eai-actions">
          <button
            type="button"
            className="eai-close-btn"
            onClick={onClose}
            disabled={confirming}
          >
            {closeLabel}
          </button>
          <button
            type="button"
            className="eai-ok-btn"
            onClick={() => void onConfirm()}
            disabled={confirming}
          >
            {confirming ? "Confirming..." : confirmLabel}
          </button>
        </div>
      </div>

      <style>
        {`
        .eai-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1060;
          padding: 16px;
        }
        .eai-modal {
          width: min(480px, 100%);
          background: #ffffff;
          border-radius: 14px;
          padding: 28px 28px 22px;
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.22);
        }
        .eai-title {
          margin: 0 0 10px;
          font-size: 20px;
          font-weight: 700;
          color: #0f172a;
        }
        .eai-text {
          margin: 0 0 24px;
          font-size: 15px;
          line-height: 1.55;
          color: #475569;
        }
        .eai-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }
        .eai-close-btn,
        .eai-ok-btn {
          min-width: 88px;
          padding: 8px 18px;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
        }
        .eai-close-btn {
          background: #fff;
          border: 1px solid #d1d5db;
          color: #0f172a;
        }
        .eai-close-btn:hover:not(:disabled) {
          background: #f3f4f6;
        }
        .eai-ok-btn {
          background: #2d63eb;
          border: none;
          color: #fff;
        }
        .eai-ok-btn:hover:not(:disabled) {
          background: #1f4fd4;
        }
        .eai-close-btn:disabled,
        .eai-ok-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }
      `}
      </style>
    </div>
  );
};

export default ExistingActiveIdentityConfirmModal;
